import { RecurrenceRule, type RecurrenceRuleInput } from './api/tasks';

/** A calendar date with its ISO weekday (1 = Monday … 7 = Sunday). */
export type LocalDateParts = { year: number; month: number; day: number; weekday: number };

const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;
const WEEKDAY_SHORT_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;
const MONTH_SHORT_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;
const ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth'] as const;
const WORK_WEEK = [1, 2, 3, 4, 5];

/** The date in the runtime's own time zone (in the browser: the user's). */
export function localDateParts(date: Date): LocalDateParts {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    weekday: ((date.getDay() + 6) % 7) + 1,
  };
}

/**
 * The stored shape of a rule: schema defaults applied, weekly with sorted unique `byWeekday` (default the first
 * occurrence's weekday), monthly with `monthlyMode`, other frequencies with neither. Throws on an invalid rule.
 */
export function normalizeRecurrence(rule: RecurrenceRuleInput, first: LocalDateParts): RecurrenceRule {
  const parsed = RecurrenceRule.parse(rule);
  const base = { freq: parsed.freq, interval: parsed.interval };
  switch (parsed.freq) {
    case 'weekly': {
      const byWeekday = [...new Set(parsed.byWeekday ?? [first.weekday])].sort((a, b) => a - b);
      return { ...base, byWeekday, ends: parsed.ends };
    }
    case 'monthly':
      return { ...base, monthlyMode: parsed.monthlyMode ?? 'day_of_month', ends: parsed.ends };
    default:
      return { ...base, ends: parsed.ends };
  }
}

function weekdayName(weekday: number): string {
  return WEEKDAY_NAMES[weekday - 1] ?? '';
}

function every(interval: number, single: string, unit: string): string {
  return interval === 1 ? single : `Every ${interval} ${unit}`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function describeWeekdays(byWeekday: number[], interval: number): string {
  if (
    interval === 1 &&
    byWeekday.length === WORK_WEEK.length &&
    WORK_WEEK.every((d, i) => byWeekday[i] === d)
  ) {
    return 'Every weekday (Monday to Friday)';
  }
  const days =
    byWeekday.length === 7
      ? 'every day'
      : byWeekday.length === 1
        ? weekdayName(byWeekday[0] ?? 1)
        : byWeekday.map((d) => WEEKDAY_SHORT_NAMES[d - 1] ?? '').join(', ');
  return `${every(interval, 'Weekly', 'weeks')} on ${days}`;
}

function describeFrequency(rule: RecurrenceRule, first: LocalDateParts): string {
  switch (rule.freq) {
    case 'daily':
      return every(rule.interval, 'Daily', 'days');
    case 'weekly':
      return describeWeekdays(rule.byWeekday ?? [first.weekday], rule.interval);
    case 'monthly': {
      const prefix = every(rule.interval, 'Monthly', 'months');
      switch (rule.monthlyMode) {
        case 'weekday_of_month':
          return `${prefix} on the ${ORDINALS[Math.ceil(first.day / 7) - 1] ?? 'fifth'} ${weekdayName(first.weekday)}`;
        case 'last_weekday_of_month':
          return `${prefix} on the last ${weekdayName(first.weekday)}`;
        default:
          return `${prefix} on day ${first.day}`;
      }
    }
    case 'yearly':
      return `${every(rule.interval, 'Annually', 'years')} on ${MONTH_NAMES[first.month - 1] ?? ''} ${first.day}`;
  }
}

function describeEnds(ends: RecurrenceRule['ends']): string {
  switch (ends.type) {
    case 'never':
      return '';
    case 'after':
      return ends.count === 1 ? ', once' : `, ${ends.count} times`;
    case 'on': {
      // Read the parts off the string: `new Date('2026-10-30')` is UTC midnight, the previous day west of Greenwich.
      const [year, month, day] = ends.date.split('-').map(Number);
      return `, until ${MONTH_SHORT_NAMES[(month ?? 1) - 1] ?? ''} ${day}, ${year}`;
    }
  }
}

/** Google-style summary, e.g. "Weekly on Monday, 10 times", for a rule whose first occurrence is on `first`. */
export function describeRecurrence(rule: RecurrenceRuleInput, first: LocalDateParts): string {
  const normalized = normalizeRecurrence(rule, first);
  return describeFrequency(normalized, first) + describeEnds(normalized.ends);
}

export type RecurrencePreset = { key: string; label: string; rule: RecurrenceRule };

/** The repeat menu offered for an entry starting on `first` (Google's "Does not repeat" list, minus custom). */
export function recurrencePresets(first: LocalDateParts): RecurrencePreset[] {
  const rules: { key: string; rule: RecurrenceRuleInput }[] = [
    { key: 'daily', rule: { freq: 'daily' } },
    { key: 'weekly', rule: { freq: 'weekly', byWeekday: [first.weekday] } },
    { key: 'monthly_nth', rule: { freq: 'monthly', monthlyMode: 'weekday_of_month' } },
  ];
  // Only a date in the month's final seven days is that month's last such weekday.
  if (first.day + 7 > daysInMonth(first.year, first.month)) {
    rules.push({ key: 'monthly_last', rule: { freq: 'monthly', monthlyMode: 'last_weekday_of_month' } });
  }
  rules.push(
    { key: 'monthly_day', rule: { freq: 'monthly', monthlyMode: 'day_of_month' } },
    { key: 'yearly', rule: { freq: 'yearly' } },
    { key: 'weekdays', rule: { freq: 'weekly', byWeekday: WORK_WEEK } },
  );
  return rules.map(({ key, rule }) => {
    const normalized = normalizeRecurrence({ ...rule, interval: 1, ends: { type: 'never' } }, first);
    return { key, label: describeRecurrence(normalized, first), rule: normalized };
  });
}

function sameEnds(a: RecurrenceRule['ends'], b: RecurrenceRule['ends']): boolean {
  switch (a.type) {
    case 'never':
      return b.type === 'never';
    case 'on':
      return b.type === 'on' && a.date === b.date;
    case 'after':
      return b.type === 'after' && a.count === b.count;
  }
}

/** Whether two rules repeat identically for an entry starting on `first` (defaults and ordering ignored). */
export function sameRecurrence(
  a: RecurrenceRuleInput,
  b: RecurrenceRuleInput,
  first: LocalDateParts,
): boolean {
  const x = normalizeRecurrence(a, first);
  const y = normalizeRecurrence(b, first);
  const xDays = x.byWeekday ?? [];
  const yDays = y.byWeekday ?? [];
  return (
    x.freq === y.freq &&
    x.interval === y.interval &&
    x.monthlyMode === y.monthlyMode &&
    xDays.length === yDays.length &&
    xDays.every((d, i) => yDays[i] === d) &&
    sameEnds(x.ends, y.ends)
  );
}
